<template>
  <div class="flex h-full">
    <!-- Mobile drawer backdrop -->
    <Transition name="fade">
      <div
        v-if="drawerOpen"
        class="fixed inset-0 bg-black/40 z-30 md:hidden"
        @click="drawerOpen = false"
      />
    </Transition>

    <!-- Spaces panel -->
    <aside
      class="fixed top-16 bottom-16 left-0 w-56 bg-white border-r border-[var(--border)] flex flex-col z-30
             md:static md:z-0 md:bottom-auto
             transform transition-transform duration-200 ease-out shrink-0"
      :class="drawerOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'"
    >
      <!-- Panel content -->
      <nav class="flex-1 overflow-y-auto p-2 pt-3">
        <!-- Home -->
        <RouterLink
          to="/connect"
          class="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1"
          :class="isHome ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'"
          @click="drawerOpen = false"
        >
          <font-awesome-icon :icon="['fas', 'house']" class="text-xs w-4 text-center" />
          Home
        </RouterLink>

        <!-- School -->
        <RouterLink
          v-if="schoolName"
          to="/connect/school"
          class="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1"
          :class="isSchoolView ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-50'"
          @click="drawerOpen = false"
        >
          <font-awesome-icon :icon="['fas', 'bullhorn']" class="text-xs w-4 text-center" />
          {{ schoolName }}
        </RouterLink>

        <!-- Spaces section -->
        <div class="mt-3">
          <h3 class="px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            Spaces
          </h3>

          <!-- Loading -->
          <div v-if="loadingSpaces" class="space-y-1 px-1">
            <div v-for="i in 4" :key="i" class="flex items-center gap-2.5 px-2 py-1.5">
              <div class="w-5 h-5 rounded bg-gray-200 animate-pulse" />
              <div class="h-3 bg-gray-200 rounded animate-pulse flex-1" />
            </div>
          </div>

          <!-- Space list -->
          <div v-else class="space-y-0.5">
            <RouterLink
              v-for="space in spaces"
              :key="space.id"
              :to="`/connect/spaces/${space.id}`"
              class="flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors group"
              :class="activeSpaceId === space.id
                ? 'bg-green-50 text-green-700'
                : 'text-gray-700 hover:bg-gray-50'"
              @click="drawerOpen = false"
            >
              <div class="relative shrink-0">
                <span
                  class="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white"
                  :style="{ backgroundColor: getSpaceColor(space) }"
                >
                  {{ smartInitials(space.name) }}
                </span>
                <span
                  v-if="space.isEnabled"
                  class="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white"
                />
              </div>
              <span class="truncate">{{ space.name }}</span>
              <span
                v-if="space.memberCount"
                class="ml-auto text-[10px] text-gray-400 group-hover:text-gray-500 shrink-0"
              >
                {{ space.memberCount }}
              </span>
            </RouterLink>
          </div>

          <!-- Empty -->
          <p v-if="!loadingSpaces && !spaces.length" class="px-3 py-2 text-xs text-gray-400">
            No spaces yet
          </p>
        </div>
      </nav>
    </aside>

    <!-- Feed area -->
    <div class="flex-1 flex flex-col min-w-0">
      <!-- Feed header bar -->
      <div class="bg-white border-b border-gray-200 px-4 md:px-6 py-3 flex items-center gap-3 shrink-0">
        <!-- Mobile drawer toggle -->
        <button
          class="md:hidden p-1.5 -ml-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          @click="drawerOpen = !drawerOpen"
        >
          <font-awesome-icon :icon="['fas', 'bars']" />
        </button>

        <!-- Space avatar (when viewing a space) -->
        <div
          v-if="activeSpace"
          class="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white shrink-0"
          :style="{ backgroundColor: getSpaceColor(activeSpace) }"
        >
          {{ smartInitials(activeSpace.name) }}
        </div>

        <!-- Title -->
        <div class="min-w-0 flex-1">
          <h1 class="font-semibold text-gray-900 truncate">
            {{ activeSpace ? activeSpace.name : isSchoolView ? (schoolName || 'School') : 'Home' }}
          </h1>
          <p v-if="activeSpace" class="text-xs text-gray-500 flex items-center gap-1">
            <font-awesome-icon :icon="['fas', 'users']" class="text-[10px]" />
            {{ activeSpace.memberCount }} {{ activeSpace.memberCount === 1 ? 'member' : 'members' }}
          </p>
          <p v-else-if="isSchoolView" class="text-xs text-gray-500">School-wide posts</p>
          <p v-else class="text-xs text-gray-500">All spaces</p>
        </div>

        <!-- Members button (space view, teachers only) -->
        <button
          v-if="activeSpace && auth.isTeacher"
          class="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          :class="showMembers ? 'bg-gray-100 text-gray-900' : ''"
          @click="showMembers = !showMembers"
        >
          <font-awesome-icon :icon="['fas', 'user-group']" class="text-xs" />
          Members
        </button>

        <!-- Space menu (enabled spaces, teachers only) -->
        <div v-if="activeSpace && isSpaceEnabled && auth.isTeacher" class="relative hidden md:block">
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            @click.stop="showSpaceMenu = !showSpaceMenu"
          >
            <font-awesome-icon :icon="['fas', 'ellipsis-vertical']" class="text-xs" />
          </button>
          <div
            v-if="showSpaceMenu"
            class="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20"
          >
            <button
              class="w-full px-3 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2 transition-colors"
              @click="showSpaceMenu = false; showDisableConfirm = true"
            >
              <font-awesome-icon :icon="['fas', 'power-off']" class="text-xs w-4" />
              Disable space
            </button>
          </div>
        </div>
      </div>

      <!-- Tab bar (hidden when only one tab visible) -->
      <div v-if="tabs.length > 1" class="bg-white border-b border-gray-200 px-4 md:px-6 flex items-center gap-6 shrink-0">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="relative py-2.5 text-sm font-medium transition-colors"
          :class="activeTab === tab.key
            ? 'text-gray-900'
            : 'text-gray-400 hover:text-gray-600'"
          @click="activeTab = tab.key"
        >
          <div class="flex items-center gap-1.5">
            <font-awesome-icon :icon="tab.icon" class="text-xs" />
            {{ tab.label }}
          </div>
          <div
            v-if="activeTab === tab.key"
            class="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 rounded-full"
          />
        </button>
      </div>

      <!-- Content area with optional members panel -->
      <div class="flex-1 flex min-h-0">

        <!-- ═══ POSTS TAB ═══ -->
        <template v-if="activeTab === 'posts'">
          <div class="flex-1 flex flex-col min-w-0">
            <!-- Filter bar -->
            <div class="bg-white border-b border-gray-100 px-4 md:px-6 py-2 flex items-center gap-1.5 overflow-x-auto scrollbar-hide shrink-0">
              <button
                v-for="filter in filterOptions"
                :key="filter.key"
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border"
                :class="activeFilter === filter.key
                  ? filterActiveClasses[filter.color]
                  : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300'"
                @click="activeFilter = filter.key"
              >
                <font-awesome-icon :icon="filter.icon" class="text-[10px]" />
                {{ filter.label }}
                <span
                  v-if="(filterCounts[filter.key] ?? 0) > 0"
                  class="min-w-[1rem] h-4 flex items-center justify-center rounded-full text-[10px] font-semibold px-1"
                  :class="activeFilter === filter.key
                    ? 'bg-white/25 text-white'
                    : filterBadgeClasses[filter.color]"
                >
                  {{ filterCounts[filter.key] ?? 0 }}
                </span>
              </button>
            </div>

            <!-- Feed scroll area -->
            <div class="flex-1 overflow-y-auto bg-gray-50/50">
              <div class="max-w-2xl mx-auto px-4 md:px-6 py-4 pb-20 md:pb-6">
                <!-- Enable banner (disabled space, teachers only) -->
                <div
                  v-if="activeSpace && !isSpaceEnabled && auth.isTeacher"
                  class="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex items-center justify-between gap-4"
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <div class="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                      <font-awesome-icon :icon="['fas', 'circle-exclamation']" class="text-amber-600 text-sm" />
                    </div>
                    <div class="min-w-0">
                      <p class="text-sm font-semibold text-gray-900">This space is not enabled yet</p>
                      <p class="text-xs text-gray-500 mt-0.5">Enable it to start posting and inviting members.</p>
                    </div>
                  </div>
                  <button
                    class="shrink-0 px-4 py-2 bg-emerald-500 text-white font-medium rounded-xl hover:bg-emerald-600 transition-colors text-sm flex items-center gap-2"
                    :disabled="enablingSpace"
                    @click="handleEnableSpace"
                  >
                    <div v-if="enablingSpace" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <font-awesome-icon v-else :icon="['fas', 'power-off']" class="text-xs" />
                    Enable Space
                  </button>
                </div>

                <!-- Inline compose (teachers only) — collapsed prompt expands in place -->
                <div v-if="auth.isTeacher && !loadingPosts && (!activeSpace || isSpaceEnabled)" class="mb-5">
                  <InlineCompose
                    :key="activeSpaceId || (isSchoolView ? 'school' : 'home')"
                    :spaces="spaces"
                    :active-space-id="activeSpaceId"
                    :active-space-name="activeSpace?.name"
                    :teacher-name="auth.userName"
                    :is-teacher="auth.isTeacher"
                    :members="activeSpaceId ? members : []"
                    @posted="onPostCreated()"
                  />
                </div>

                <!-- Loading skeleton -->
                <div v-if="loadingPosts && !posts.length" class="space-y-3">
                  <div v-for="i in 3" :key="i" class="bg-white rounded-xl border border-gray-200 p-5">
                    <div class="flex items-center gap-2.5 mb-3">
                      <div class="h-5 w-24 bg-gray-200 rounded-lg animate-pulse" />
                      <div class="h-3 w-12 bg-gray-100 rounded animate-pulse" />
                    </div>
                    <div class="flex items-center gap-2.5 mb-3">
                      <div class="w-9 h-9 rounded-lg bg-gray-200 animate-pulse shrink-0" />
                      <div class="space-y-1.5">
                        <div class="h-4 bg-gray-200 rounded animate-pulse w-32" />
                        <div class="h-3 bg-gray-100 rounded animate-pulse w-20" />
                      </div>
                    </div>
                    <div class="space-y-2">
                      <div class="h-3 bg-gray-200 rounded animate-pulse w-full" />
                      <div class="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
                    </div>
                  </div>
                </div>

                <!-- Empty state -->
                <div
                  v-else-if="!loadingPosts && !filteredPosts.length"
                  class="bg-white rounded-xl border border-gray-200 p-12 text-center"
                >
                  <div class="w-14 h-14 rounded-xl bg-green-50 flex items-center justify-center mx-auto mb-3">
                    <font-awesome-icon :icon="['fas', 'comments']" class="text-xl text-green-400" />
                  </div>
                  <h3 class="font-semibold text-gray-900 mb-1 text-sm">
                    {{ activeFilter === 'ALL' ? 'No posts yet' : 'No matching posts' }}
                  </h3>
                  <p v-if="auth.isTeacher" class="text-sm text-gray-500">
                    {{ activeFilter === 'ALL'
                      ? (activeSpace ? 'Start the conversation' : 'Be the first to share something')
                      : 'No posts match the selected filter' }}
                  </p>
                  <p v-else class="text-sm text-gray-500">
                    {{ activeFilter === 'ALL' ? "Your teachers haven't posted anything yet" : 'No posts match the selected filter' }}
                  </p>
                </div>

                <!-- Posts — individual cards -->
                <div v-if="filteredPosts.length" class="space-y-3">
                  <PostCard
                    v-for="post in filteredPosts"
                    :key="post.id"
                    :post="post"
                    :space-name="post.spaceId ? spaceNameMap[post.spaceId] : undefined"
                    :show-space-badge="!activeSpaceId"
                    :current-user-id="currentUserId"
                    :student-name-map="studentNameMap"
                    :is-teacher="auth.isTeacher"
                    @edit="openEdit"
                    @delete="confirmDelete"
                    @pin="togglePin"
                  />
                </div>

                <!-- Load more -->
                <div v-if="hasMore && filteredPosts.length && activeFilter === 'ALL'" class="flex justify-center pt-4">
                  <button
                    class="px-5 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 bg-white border border-gray-200 hover:border-gray-300 rounded-lg transition-colors"
                    :disabled="loadingPosts"
                    @click="loadMore"
                  >
                    <div
                      v-if="loadingPosts"
                      class="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto"
                    />
                    <span v-else>Load more</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Members side panel (space view, teachers only, desktop) -->
          <aside
            v-if="showMembers && activeSpace && auth.isTeacher"
            class="hidden md:block w-72 border-l border-gray-200 bg-white overflow-y-auto shrink-0"
          >
            <SpaceMembersPanel
              :members="members"
              :loading="loadingMembers"
              :inviting-student-id="invitingStudentId"
              :inviting-all="invitingAll"
              @invite="handleInvite"
              @invite-all="handleInviteAll"
            />
          </aside>
        </template>

        <!-- ═══ ASSIGNMENTS TAB ═══ -->
        <template v-if="activeTab === 'assignments'">
          <div class="flex-1 overflow-y-auto bg-gray-50/50">
            <div class="max-w-2xl mx-auto px-4 md:px-6 py-4 pb-20 md:pb-6">
              <!-- Post Assignment button (teachers only, space view) -->
              <button
                v-if="auth.isTeacher && activeSpace && isSpaceEnabled"
                class="w-full mb-5 flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--primary-600)] hover:bg-[var(--primary-700)] text-white font-medium rounded-xl transition-colors text-sm"
                @click="showPostAssignmentModal = true"
              >
                <font-awesome-icon :icon="['fas', 'file-lines']" class="text-xs" />
                Post Assignment
              </button>

              <!-- Loading -->
              <div v-if="loadingPosts && !posts.length" class="flex justify-center py-12">
                <div class="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              </div>

              <!-- Empty state -->
              <div
                v-else-if="!assignmentPosts.length"
                class="bg-white rounded-xl border border-gray-200 p-12 text-center"
              >
                <div class="w-14 h-14 rounded-xl bg-cyan-50 flex items-center justify-center mx-auto mb-3">
                  <font-awesome-icon :icon="['fas', 'file-lines']" class="text-xl text-cyan-400" />
                </div>
                <h3 class="font-semibold text-gray-900 mb-1 text-sm">No assignments posted yet</h3>
                <p v-if="auth.isTeacher" class="text-sm text-gray-500">
                  Post assignments from your gradebook to share them with your class.
                </p>
                <p v-else class="text-sm text-gray-500">
                  Your teacher hasn't posted any assignments yet.
                </p>
              </div>

              <!-- Assignment cards -->
              <div v-else class="space-y-3">
                <ConnectAssignmentCard
                  v-for="post in assignmentPosts"
                  :key="post.id"
                  :assignment="post.assignment!"
                  :description="post.body?.trim() || undefined"
                  :is-teacher="auth.isTeacher"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Edit Post Modal -->
    <CreatePostModal
      v-if="editingPost"
      v-model="showCreateModal"
      :editing-post="editingPost"
      @updated="onPostUpdated"
    />

    <!-- Post Assignment Modal -->
    <PostAssignmentModal
      v-if="activeSpace?.classId"
      v-model="showPostAssignmentModal"
      :space-id="activeSpaceId!"
      :class-id="activeSpace.classId"
      :existing-posts="posts"
      @posted="onPostCreated"
    />

    <!-- Delete confirmation -->
    <Teleport to="body">
      <div
        v-if="deletingPost"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div class="absolute inset-0 bg-black/50" @click="deletingPost = null" />
        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">Delete Post</h3>
          <p class="text-sm text-gray-600 mb-4">
            Are you sure? This action cannot be undone.
          </p>
          <div
            v-if="deleteError"
            class="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
          >
            {{ deleteError }}
          </div>
          <div class="flex justify-end gap-3">
            <button
              class="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
              @click="deletingPost = null; deleteError = null"
            >
              Cancel
            </button>
            <button
              class="px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
              :disabled="deleting"
              @click="executeDelete"
            >
              <div
                v-if="deleting"
                class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
              />
              Delete
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Disable space confirmation -->
    <Teleport to="body">
      <div
        v-if="showDisableConfirm"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div class="absolute inset-0 bg-black/50" @click="showDisableConfirm = false" />
        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
          <div class="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
            <font-awesome-icon :icon="['fas', 'power-off']" class="text-amber-600" />
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">Disable Space</h3>
          <p class="text-sm text-gray-600 mb-1">
            Students and parents will no longer be able to see this space or its posts.
          </p>
          <p class="text-sm text-gray-500 mb-6">
            All posts will be kept and will be visible again if you re-enable the space.
          </p>
          <div class="flex justify-end gap-3">
            <button
              class="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
              @click="showDisableConfirm = false"
            >
              Cancel
            </button>
            <button
              class="px-4 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2"
              :disabled="disablingSpace"
              @click="handleDisableSpace"
            >
              <div
                v-if="disablingSpace"
                class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
              />
              Disable
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useAnalytics } from '@/composables/useAnalytics'
import { connectApi } from '@/api'
import type { SpaceSummary, SpacePost, SpaceMember } from '@/api'
import { useAuthStore } from '@/store/AuthStore'
import PostCard from '@/components/connect/PostCard.vue'
import ConnectAssignmentCard from '@/components/connect/ConnectAssignmentCard.vue'
import InlineCompose from '@/components/connect/InlineCompose.vue'
import CreatePostModal from '@/components/connect/CreatePostModal.vue'
import PostAssignmentModal from '@/components/connect/PostAssignmentModal.vue'
import SpaceMembersPanel from '@/components/connect/SpaceMembersPanel.vue'

const route = useRoute()
const { capture } = useAnalytics()
const auth = useAuthStore()
const currentUserId = computed(() => auth.currentUser?.id)

// --- Spaces panel state ---
const spaces = ref<SpaceSummary[]>([])
const loadingSpaces = ref(true)
const drawerOpen = ref(false)

const isHome = computed(() => route.path === '/connect')
const isSchoolView = computed(() => route.path === '/connect/school')
const activeSpaceId = computed(() => route.params.id as string | undefined)
const schoolName = ref<string | null>(null)
const activeSpace = computed(() =>
  activeSpaceId.value ? spaces.value.find(s => s.id === activeSpaceId.value) ?? spaceDetail.value : null,
)

// For spaces not yet in the list (direct navigation)
const spaceDetail = ref<SpaceSummary | null>(null)

// --- Tab state ---
type TabKey = 'posts' | 'assignments'
const activeTab = ref<TabKey>('posts')
const tabs = computed(() => {
  const list: { key: TabKey; label: string; icon: string[] }[] = [
    { key: 'posts', label: 'Posts', icon: ['fas', 'comments'] },
  ]
  if (import.meta.env.DEV) {
    list.push({ key: 'assignments', label: 'Assignments', icon: ['fas', 'file-lines'] })
  }
  return list
})

// --- Feed state ---
const posts = ref<SpacePost[]>([])
const loadingPosts = ref(true)
const page = ref(0)
const hasMore = ref(true)

const showCreateModal = ref(false)
const showPostAssignmentModal = ref(false)
const editingPost = ref<SpacePost | null>(null)
const deletingPost = ref<SpacePost | null>(null)
const deleting = ref(false)
const deleteError = ref<string | null>(null)

// --- Enable/disable state ---
const enablingSpace = ref(false)
const disablingSpace = ref(false)
const showDisableConfirm = ref(false)
const showSpaceMenu = ref(false)
const isSpaceEnabled = computed(() => activeSpace.value?.isEnabled !== false)

// --- Members state ---
const members = ref<SpaceMember[]>([])
const loadingMembers = ref(true)
const showMembers = ref(false)
const invitingStudentId = ref<string | null>(null)
const invitingAll = ref(false)

// --- Filter state ---
type FilterKey = 'ALL' | 'ANNOUNCEMENT' | 'REMINDER' | 'MOMENT' | 'EVENT'
const activeFilter = ref<FilterKey>('ALL')

const filterOptions: { key: FilterKey; label: string; icon: string[]; color: string }[] = [
  { key: 'ALL', label: 'All', icon: ['fas', 'layer-group'], color: 'gray' },
  { key: 'ANNOUNCEMENT', label: 'Announcements', icon: ['fas', 'bullhorn'], color: 'blue' },
  { key: 'EVENT', label: 'Events', icon: ['fas', 'calendar-day'], color: 'purple' },
  { key: 'REMINDER', label: 'Reminders', icon: ['fas', 'bell'], color: 'amber' },
  { key: 'MOMENT', label: 'Moments', icon: ['fas', 'camera'], color: 'green' },
]

const assignmentPosts = computed(() =>
  posts.value.filter(p => p.type === 'ASSIGNMENT' && p.assignment)
)

const filterActiveClasses: Record<string, string> = {
  gray: 'bg-gray-700 border-gray-700 text-white shadow-sm',
  blue: 'bg-blue-500 border-blue-500 text-white shadow-sm shadow-blue-200/50',
  amber: 'bg-amber-500 border-amber-500 text-white shadow-sm shadow-amber-200/50',
  slate: 'bg-slate-600 border-slate-600 text-white shadow-sm shadow-slate-200/50',
  purple: 'bg-purple-500 border-purple-500 text-white shadow-sm shadow-purple-200/50',
  green: 'bg-emerald-500 border-emerald-500 text-white shadow-sm shadow-emerald-200/50',
}

const filterBadgeClasses: Record<string, string> = {
  gray: 'bg-gray-100 text-gray-600',
  blue: 'bg-blue-100 text-blue-600',
  amber: 'bg-amber-100 text-amber-700',
  slate: 'bg-slate-100 text-slate-600',
  purple: 'bg-purple-100 text-purple-600',
  green: 'bg-emerald-100 text-emerald-600',
}

const filterCounts = computed(() => {
  const counts: Record<string, number> = { ALL: posts.value.length }
  for (const option of filterOptions) {
    if (option.key !== 'ALL') {
      counts[option.key] = posts.value.filter(p => p.type === option.key).length
    }
  }
  return counts
})

const filteredPosts = computed(() => {
  if (activeFilter.value === 'ALL') return posts.value
  return posts.value.filter(p => p.type === activeFilter.value)
})

const spaceNameMap = computed(() => {
  const map: Record<string, string> = {}
  for (const space of spaces.value) {
    map[space.id] = space.name
  }
  return map
})

const studentNameMap = computed(() => {
  const map: Record<string, string> = {}
  for (const m of members.value) {
    if (m.studentId) map[m.studentId] = m.name
  }
  return map
})

// --- Helpers ---
import { getColourHex } from '@/constants/colors'

function getSpaceColor(space: { colour?: string; name: string }): string {
  if (space.colour) return getColourHex(space.colour)
  const fallbackColors = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#6366F1']
  let hash = 0
  for (let i = 0; i < space.name.length; i++) {
    hash = space.name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return fallbackColors[Math.abs(hash) % fallbackColors.length]!
}

function smartInitials(name: string): string {
  const words = name.trim().split(/\s+/)
  const numMatch = name.match(/\d+/)
  if (numMatch) {
    const nonNumWord = words.find(w => !/^\d+$/.test(w))
    if (nonNumWord) return numMatch[0] + nonNumWord[0]!.toUpperCase()
    return numMatch[0]!.substring(0, 2)
  }
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase()
  return name.substring(0, 2).toUpperCase()
}

// --- Data loading ---
async function loadSpaces() {
  loadingSpaces.value = true
  try {
    const response = await connectApi.getSpaces()
    spaces.value = response.spaces
    schoolName.value = response.schoolName
  } catch (err) {
    console.error('Failed to load spaces:', err)
  } finally {
    loadingSpaces.value = false
  }
}

async function loadSpaceDetail() {
  if (!activeSpaceId.value) return
  // Already in the list?
  if (spaces.value.find(s => s.id === activeSpaceId.value)) {
    spaceDetail.value = null
    return
  }
  try {
    spaceDetail.value = await connectApi.getSpace(activeSpaceId.value)
  } catch (err) {
    console.error('Failed to load space detail:', err)
  }
}

async function handleEnableSpace() {
  if (!activeSpaceId.value) return
  enablingSpace.value = true
  try {
    await connectApi.enableSpace(activeSpaceId.value)
    await loadSpaces()
  } catch (err) {
    console.error('Failed to enable space:', err)
  } finally {
    enablingSpace.value = false
  }
}

async function handleDisableSpace() {
  if (!activeSpaceId.value) return
  disablingSpace.value = true
  try {
    await connectApi.disableSpace(activeSpaceId.value)
    await loadSpaces()
    showDisableConfirm.value = false
  } catch (err) {
    console.error('Failed to disable space:', err)
  } finally {
    disablingSpace.value = false
  }
}

async function loadPosts(reset = false) {
  if (reset) {
    page.value = 0
    posts.value = []
    hasMore.value = true
  }
  loadingPosts.value = true
  try {
    let newPosts: SpacePost[]
    if (activeSpaceId.value) {
      newPosts = await connectApi.getSpacePosts(activeSpaceId.value, page.value, 20)
    } else if (isSchoolView.value) {
      newPosts = await connectApi.getFeed(page.value, 20, true)
    } else {
      newPosts = await connectApi.getFeed(page.value, 20)
    }
    if (reset) {
      posts.value = newPosts
    } else {
      posts.value.push(...newPosts)
    }
    hasMore.value = newPosts.length === 20
  } catch (err) {
    console.error('Failed to load posts:', err)
  } finally {
    loadingPosts.value = false
  }
}

async function loadMembers() {
  if (!activeSpaceId.value || !auth.isTeacher) return
  loadingMembers.value = true
  try {
    members.value = await connectApi.getSpaceMembers(activeSpaceId.value)
  } catch (err) {
    console.error('Failed to load members:', err)
  } finally {
    loadingMembers.value = false
  }
}

function loadMore() {
  page.value++
  loadPosts()
}

// --- Post actions ---
function openEdit(post: SpacePost) {
  editingPost.value = post
  showCreateModal.value = true
}

function confirmDelete(post: SpacePost) {
  deletingPost.value = post
}

async function executeDelete() {
  if (!deletingPost.value) return
  deleting.value = true
  deleteError.value = null
  try {
    if (!deletingPost.value.spaceId) {
      await connectApi.deleteSchoolWidePost(deletingPost.value.id)
    } else {
      await connectApi.deletePost(deletingPost.value.spaceId, deletingPost.value.id)
    }
    posts.value = posts.value.filter(p => p.id !== deletingPost.value!.id)
    deletingPost.value = null
  } catch (err) {
    console.error('Failed to delete post:', err)
    deleteError.value = 'Failed to delete post. Please try again.'
  } finally {
    deleting.value = false
  }
}

async function togglePin(post: SpacePost) {
  try {
    if (!post.spaceId) return
    const updated = await connectApi.pinPost(post.spaceId, post.id, !post.isPinned)
    const idx = posts.value.findIndex(p => p.id === post.id)
    if (idx !== -1) posts.value[idx] = updated
    posts.value.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1
      if (!a.isPinned && b.isPinned) return 1
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  } catch (err) {
    console.error('Failed to pin/unpin post:', err)
  }
}

function onPostCreated() {
  loadPosts(true)
}

function onPostUpdated() {
  editingPost.value = null
  loadPosts(true)
}

// --- Invite actions ---
async function handleInvite(member: SpaceMember) {
  if (!member.studentId) return
  invitingStudentId.value = member.studentId
  try {
    await connectApi.inviteStudent(member.studentId)
    capture('connect_parent_invited', { invite_method: 'email' })
    await loadMembers()
  } catch (err) {
    console.error('Failed to invite student:', err)
  } finally {
    invitingStudentId.value = null
  }
}

async function handleInviteAll() {
  if (!activeSpaceId.value) return
  invitingAll.value = true
  try {
    await connectApi.inviteAllStudents(activeSpaceId.value)
    capture('connect_parent_invited', { invite_method: 'email' })
    await loadMembers()
  } catch (err) {
    console.error('Failed to invite all:', err)
  } finally {
    invitingAll.value = false
  }
}

// --- Route change handling ---
function onRouteChange() {
  activeTab.value = 'posts'
  activeFilter.value = 'ALL'
  showMembers.value = false
  showSpaceMenu.value = false
  loadPosts(true)
  if (activeSpaceId.value) {
    capture('connect_space_viewed')
    loadSpaceDetail()
    loadMembers()
  } else {
    members.value = []
    spaceDetail.value = null
  }
}

watch(() => route.path, onRouteChange)

function handleClickOutside() {
  if (showSpaceMenu.value) showSpaceMenu.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  loadSpaces()
  loadPosts()
  if (activeSpaceId.value) {
    loadSpaceDetail()
    loadMembers()
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.ml-13 {
  margin-left: 3.25rem;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
